import { useRef, useState } from "react";
import { Camera, Images, X, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

/** Photo picker button that first shows a bottom sheet with two options:
 *   - Kamera öffnen  -> capture="environment"
 *   - Vom Gerät auswählen  -> multiple gallery pick
 *  Calls onFiles(FileList | File[]) when files are selected. */
export default function PhotoPicker({ onFiles, disabled = false }) {
    const [open, setOpen] = useState(false);
    const camRef = useRef(null);
    const galRef = useRef(null);

    const handleChange = (e) => {
        const files = e.target.files;
        if (files && files.length) onFiles?.(files);
        e.target.value = "";
        setOpen(false);
    };

    return (
        <>
            <button
                type="button"
                data-testid="btn-add-photo"
                disabled={disabled}
                onClick={() => setOpen(true)}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-mes-brand px-3 text-xs font-semibold text-white hover:bg-mes-brandDark disabled:opacity-50"
            >
                <Plus className="h-4 w-4" strokeWidth={2.5} /> Foto hinzufügen
            </button>

            <input
                ref={camRef} type="file" accept="image/*" capture="environment" hidden
                onChange={handleChange} data-testid="input-camera"
            />
            <input
                ref={galRef} type="file" accept="image/*" multiple hidden
                onChange={handleChange} data-testid="input-gallery"
            />

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="bottom" className="rounded-t-2xl border-t border-mes-border bg-white p-6" data-testid="photo-picker-sheet">
                    <SheetHeader>
                        <SheetTitle className="font-display text-mes-ink">Foto hinzufügen</SheetTitle>
                        <SheetDescription>Kamera öffnen oder ein Bild vom Gerät auswählen.</SheetDescription>
                    </SheetHeader>
                    <div className="mt-4 grid gap-3">
                        <button
                            type="button" data-testid="pick-camera"
                            onClick={() => camRef.current?.click()}
                            className="flex items-center gap-3 rounded-lg border border-mes-border bg-white px-4 py-4 text-left hover:border-mes-brand hover:bg-mes-brandSoft"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-mes-brandSoft text-mes-brand">
                                <Camera className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-semibold text-mes-ink">Kamera öffnen</div>
                                <div className="text-xs text-mes-muted">Neues Foto aufnehmen</div>
                            </div>
                        </button>
                        <button
                            type="button" data-testid="pick-gallery"
                            onClick={() => galRef.current?.click()}
                            className="flex items-center gap-3 rounded-lg border border-mes-border bg-white px-4 py-4 text-left hover:border-mes-brand hover:bg-mes-brandSoft"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-mes-brandSoft text-mes-brand">
                                <Images className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-semibold text-mes-ink">Vom Gerät auswählen</div>
                                <div className="text-xs text-mes-muted">Ein oder mehrere Bilder aus der Galerie</div>
                            </div>
                        </button>
                        <button
                            type="button" data-testid="pick-cancel"
                            onClick={() => setOpen(false)}
                            className="mt-1 inline-flex items-center justify-center gap-1 rounded-md border border-mes-border bg-white px-3 py-2 text-sm text-mes-muted hover:text-mes-ink"
                        >
                            <X className="h-4 w-4" /> Abbrechen
                        </button>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
